// ============================================================================
// ADMIN LAYOUT COMPONENT
// Left sidebar navigation layout for catalog, admin, and compliance pages
// Matches the ProjectDetail sidebar pattern
// ============================================================================

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FolderOpen,
  FileText,
  Users,
  ClipboardCheck,
  Shield,
  User,
  LogOut,
  DollarSign,
  Server,
  ExternalLink,
  Building2,
  HelpCircle,
  CalendarPlus,
  CalendarDays,
  CalendarRange,
  BadgeCheck,
  Tag,
  Workflow,
  BarChart3,
  Bell,
  Activity,
  Sparkles,
  ChevronDown,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useHelp } from './help/HelpContext';
import { useNotifications } from '../hooks/useNotifications';

interface NavItem {
  key: string;
  label: string;
  icon: React.ElementType;
  path: string;
  roles?: string[];
}

interface NavSection {
  label: string;
  items: NavItem[];
}

const navSections: NavSection[] = [
  {
    label: 'Workspace',
    items: [
      { key: 'notifications', label: 'Notifications', icon: Bell, path: '/notifications' },
      { key: 'projects', label: 'Projects', icon: FolderOpen, path: '/projects' },
      { key: 'showcase', label: 'Showcase', icon: Sparkles, path: '/showcase' },
    ],
  },
  {
    label: 'Content',
    items: [
      { key: 'avatars', label: 'Avatars', icon: Users, path: '/avatars', roles: ['avatar_admin', 'system_admin'] },
      { key: 'miro-templates', label: 'Miro Templates', icon: ExternalLink, path: '/admin/miro-templates', roles: ['system_admin'] },
      { key: 'reviews', label: 'Reviews', icon: ClipboardCheck, path: '/admin/reviews', roles: ['avatar_admin', 'template_admin', 'system_admin'] },
      { key: 'templates', label: 'Templates', icon: FileText, path: '/templates', roles: ['normal', 'avatar_admin', 'template_admin', 'system_admin'] },
    ],
  },
  {
    label: 'Studio',
    items: [
      { key: 'studio-book', label: 'Book a Session', icon: CalendarPlus, path: '/studio/book' },
      { key: 'studio-bookings', label: 'My Bookings', icon: CalendarDays, path: '/studio/bookings' },
      { key: 'studio-review', label: 'Review Queue', icon: ClipboardCheck, path: '/studio/review', roles: ['tier1_facilitator', 'system_admin'] },
      { key: 'studio-calendar', label: 'Room Calendar', icon: CalendarRange, path: '/studio/calendar', roles: ['tier1_facilitator', 'system_admin'] },
    ],
  },
  {
    label: 'Operations',
    items: [
      { key: 'active-sessions', label: 'Active Sessions', icon: Activity, path: '/admin/sessions', roles: ['system_admin'] },
      { key: 'analytics', label: 'Analytics', icon: BarChart3, path: '/admin/analytics', roles: ['system_admin'] },
      { key: 'audit-logs', label: 'Audit Logs', icon: Shield, path: '/compliance/audit-logs', roles: ['system_admin'] },
      { key: 'llm-costs', label: 'LLM Costs', icon: DollarSign, path: '/admin/llm-costs', roles: ['system_admin'] },
      { key: 'ops-health', label: 'Ops Health', icon: Server, path: '/admin/ops', roles: ['system_admin'] },
    ],
  },
  {
    label: 'Configuration',
    items: [
      { key: 'event-types', label: 'Event Types', icon: Tag, path: '/admin/event-types', roles: ['tier1_facilitator', 'system_admin'] },
      { key: 'facilitators', label: 'Facilitators', icon: BadgeCheck, path: '/admin/facilitators', roles: ['tier1_facilitator', 'system_admin'] },
      { key: 'fleet', label: 'Fleet & Devices', icon: Server, path: '/admin/fleet', roles: ['system_admin'] },
      { key: 'rooms', label: 'Rooms', icon: Building2, path: '/admin/rooms', roles: ['system_admin'] },
      { key: 'user-management', label: 'User Management', icon: User, path: '/admin/users', roles: ['system_admin'] },
      { key: 'workflow-config', label: 'Workflow Config', icon: Workflow, path: '/admin/workflow-config', roles: ['system_admin'] },
    ],
  },
];

interface AdminLayoutProps {
  children: React.ReactNode;
}

export const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { openHelp } = useHelp();

  const { unreadCount } = useNotifications();

  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem('nav-collapsed-sections');
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch {
      return new Set();
    }
  });

  const toggleSection = (label: string) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      try {
        localStorage.setItem('nav-collapsed-sections', JSON.stringify([...next]));
      } catch {}
      return next;
    });
  };

  const userInitials = user?.displayName
    ? user.displayName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)
    : 'U';

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const isVisible = (item: NavItem): boolean => {
    if (!item.roles) return true;
    if (!user) return false;
    return item.roles.includes(user.role);
  };

  const allPaths = navSections.flatMap(s => s.items.filter(isVisible).map(i => i.path));

  const isActive = (item: NavItem): boolean => {
    // Exact match for root paths
    if (item.path === '/projects') {
      return location.pathname === '/projects' || location.pathname === '/';
    }
    // Check if current path starts with this item's path
    if (!location.pathname.startsWith(item.path)) return false;
    // If a more specific (longer) nav path also matches, this item is NOT active
    const hasMoreSpecificMatch = allPaths.some(
      p => p !== item.path && p.startsWith(item.path) && location.pathname.startsWith(p)
    );
    return !hasMoreSpecificMatch;
  };

  return (
    <div className="h-screen bg-surface-light text-slate-900 flex flex-col font-display overflow-hidden">
      {/* Top bar */}
      <header className="flex items-center justify-between bg-navy px-6 py-3 shrink-0 h-16 shadow-md z-50">
        <div className="flex items-center gap-4 text-white">
          <img
            src="/spectrum.svg"
            alt="Spectrum Logo"
            className="size-8 cursor-pointer"
            onClick={() => navigate('/projects')}
          />
          <h1
            className="text-white text-lg font-extrabold tracking-tight cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => navigate('/projects')}
          >
            Co-Creation Platform
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleLogout}
            className="flex items-center justify-center rounded-lg h-8 w-8 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white border border-white/10 transition-colors"
            title="Sign out"
          >
            <LogOut size={16} />
          </button>
          <button
            onClick={() => openHelp()}
            className="flex items-center justify-center rounded-lg h-8 w-8 bg-white/5 hover:bg-white/10 text-white/70 hover:text-white border border-white/10 transition-colors"
            title="Help"
          >
            <HelpCircle size={16} />
          </button>
          <div
            className="w-8 h-8 rounded-full bg-primary flex items-center justify-center text-[#052831] font-bold text-xs cursor-pointer hover:bg-primary/80 transition-colors border-2 border-primary/20"
            onClick={() => navigate('/profile')}
            title={user?.displayName || 'Profile'}
          >
            {userInitials}
          </div>
        </div>
      </header>

      {/* Body: sidebar + main */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside className="w-64 bg-sidebar-bg border-r border-slate-200 flex flex-col shrink-0 overflow-y-auto">
          <div className="p-3 flex flex-col gap-6 flex-1">
            {navSections.map((section) => {
              const visibleItems = section.items.filter(isVisible);
              if (visibleItems.length === 0) return null;

              const isCollapsed = collapsedSections.has(section.label);
              return (
                <div key={section.label} className="flex flex-col gap-1">
                  <button
                    onClick={() => toggleSection(section.label)}
                    className="flex items-center justify-between px-3 mb-1 w-full group"
                  >
                    <h2 className="text-[11px] font-bold text-primary tracking-widest uppercase">
                      {section.label}
                    </h2>
                    <ChevronDown
                      size={12}
                      className={`text-primary/60 transition-transform duration-200 ${isCollapsed ? '-rotate-90' : ''}`}
                    />
                  </button>
                  {!isCollapsed && (
                    <nav className="flex flex-col gap-1">
                      {visibleItems.map((item) => {
                        const Icon = item.icon;
                        const active = isActive(item);
                        return (
                          <button
                            key={item.key}
                            onClick={() => navigate(item.path)}
                            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all w-full ${
                              active
                                ? 'bg-white border border-primary/20 shadow-sm text-primary'
                                : 'text-slate-600 hover:bg-white hover:text-slate-900 hover:shadow-sm'
                            }`}
                          >
                            <Icon size={20} className={active ? 'text-primary' : 'text-slate-400'} />
                            <span className={`text-sm ${active ? 'font-semibold' : 'font-medium'} flex-1`}>
                              {item.label}
                            </span>
                            {item.key === 'notifications' && unreadCount > 0 && (
                              <span className="bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 min-w-[18px] text-center leading-none">
                                {unreadCount > 99 ? '99+' : unreadCount}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </nav>
                  )}
                </div>
              );
            })}
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
};
